// Time-window menu promos (happy hours, breakfast specials), keyed by shop.
//
// One config drives the menu prices, the cart re-check and (later) the server:
// - applyTimedPromosToMenu prices the menu when it loads and on a timer
//   (src/Context/ShopData.js), so open pages flip prices at the window edges.
// - repriceProduct re-checks cart items against the order time
//   (src/react/CartContainer.js), so a cart built during a window can't be
//   submitted at the promo price after it closes.
//
// No dependencies, so the orders Lambda (afa2functions, also webpack) can
// import the same file if it ever validates prices server-side.
//
// Windows are in America/Los_Angeles time, `start` inclusive, `end` exclusive.
// `days` (0 = Sunday) is optional; omit it for every day. A window whose end is
// before its start runs past midnight.
//
// Promo types:
// - percentOff: `value`% off the item; `includeModifiers` also discounts add-ons.
// - fixedPrice: the item costs `value`; add-ons stay full price. Items already
//   at or under `value` are left alone.
//
// Scope: `categories` (menu category names, case/space-insensitive) limits the
// promo to those categories; omit it for the whole menu. `excludeNameContains`
// skips items whose name contains any of the given words.
//
// Display: `name` (short, used in "Happy Hour has ended"), `headline` and
// `detail` (home page banner, src/react/TimedPromos.js). While a promo is on,
// its `categories` move to the top of the menu with a "<badge> until <end>"
// tag; a whole-menu promo shows `barText` + "until <end>" above the menu.

export const PROMOS = [
  {
    id: 'craveoaks-happy-hour',
    name: 'Happy Hour',
    headline: '30% Off Everything',
    detail: 'Every item on the menu, add-ons included.',
    barText: 'Happy Hour: 30% off everything',
    shopIds: ['craveoaks'],
    start: '15:00',
    end: '18:00',
    type: 'percentOff',
    value: 30,
    includeModifiers: true,
  },
  {
    id: 'craveoaks-breakfast',
    name: 'Breakfast Special',
    headline: '$12.99 Breakfast',
    detail: 'Any plate from our Breakfast menu (oatmeal excluded).',
    badge: '$12.99',
    shopIds: ['craveoaks'],
    start: '04:00',
    end: '10:00',
    categories: ['Breakfast'],
    excludeNameContains: ['oatmeal'],
    type: 'fixedPrice',
    value: 12.99,
  },
]

// How long after a window closes an ASAP order may still keep a promo that is
// already on its items, to cover customers who are mid-checkout at closing.
export const ASAP_GRACE_MS = 15 * 60 * 1000

const WEEKDAYS = {Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6}

function laClock(date) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Los_Angeles',
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date)
  const get = (type) => (parts.find((p) => p.type === type) || {}).value
  return {
    day: WEEKDAYS[get('weekday')],
    minutes: (Number(get('hour')) % 24) * 60 + Number(get('minute')),
  }
}

function toMinutes(hhmm) {
  const [h, m] = String(hhmm).split(':')
  return Number(h) * 60 + Number(m || 0)
}

export function isPromoActiveAt(promo, date) {
  const {day, minutes} = laClock(date)
  const start = toMinutes(promo.start)
  const end = toMinutes(promo.end)
  if (start <= end) {
    if (minutes < start || minutes >= end) { return false }
    return !promo.days || promo.days.indexOf(day) !== -1
  }
  // past midnight: the part after midnight belongs to the previous day's window
  if (minutes >= start) {
    return !promo.days || promo.days.indexOf(day) !== -1
  }
  if (minutes < end) {
    return !promo.days || promo.days.indexOf((day + 6) % 7) !== -1
  }
  return false
}

function promosForShop(shopId) {
  return PROMOS.filter((promo) => promo.shopIds.indexOf(shopId) !== -1)
}

export function hasTimedPromos(shopId) {
  return promosForShop(shopId).length > 0
}

export function getPromoById(id) {
  return PROMOS.find((promo) => promo.id === id) || null
}

function normalize(text) {
  return String(text || '').trim().toLowerCase()
}

function productName(product) {
  return (product && product.name && product.name.en) || ''
}

function promoCoversProduct(promo, product) {
  if (promo.categories) {
    const category = normalize(product.promoCategory)
    const inCategory = promo.categories.some((c) => normalize(c) === category)
    if (!inCategory) { return false }
  }
  if (promo.excludeNameContains) {
    const name = normalize(productName(product))
    if (promo.excludeNameContains.some((word) => name.indexOf(normalize(word)) !== -1)) { return false }
  }
  return true
}

// Product prices arrive as strings ("8.50"), modifier prices as numbers; empty
// or missing prices (market price, price lives on a required modifier) stay put.
function toPrice(price) {
  const value = Number(price)
  return (price === null || price === undefined || price === '' || !isFinite(value)) ? null : value
}

function percentOff(price, percent) {
  const value = toPrice(price)
  if (value === null || value <= 0) { return price }
  return Math.floor(value * (100 - percent)) / 100
}

function discountModgroups(modgroups, percent) {
  return modgroups.map((modgroup) => {
    if (!modgroup.modifiers) { return modgroup }
    return Object.assign({}, modgroup, {
      modifiers: modgroup.modifiers.map((modifier) => {
        const updated = Object.assign({}, modifier, {price: percentOff(modifier.price, percent)})
        if (Array.isArray(modifier.priceIf)) {
          updated.priceIf = modifier.priceIf.map((entry) => Object.assign({}, entry, {
            price: percentOff(entry.price, percent),
          }))
        }
        return updated
      })
    })
  })
}

// The promo-free version of a product: what was saved in promoBase when a
// promo was applied, or the product itself.
export function stripPromo(product) {
  if (!product || !product.promoBase) { return product }
  const base = product.promoBase
  const stripped = Object.assign({}, product, {price: base.price, modgroups: base.modgroups})
  delete stripped.promoBase
  delete stripped.promoId
  if (base.hasOriginalPrice) {
    stripped.originalPrice = base.originalPrice
  } else {
    delete stripped.originalPrice
  }
  if (!base.hasModgroups) { delete stripped.modgroups }
  return stripped
}

function applyPromo(baseProduct, promo) {
  const basePrice = toPrice(baseProduct.price)
  let price = baseProduct.price
  let modgroups = baseProduct.modgroups
  if (promo.type === 'percentOff') {
    price = percentOff(baseProduct.price, promo.value)
    if (promo.includeModifiers && Array.isArray(baseProduct.modgroups)) {
      modgroups = discountModgroups(baseProduct.modgroups, promo.value)
    }
  } else if (promo.type === 'fixedPrice') {
    if (basePrice === null || basePrice <= promo.value) { return null }
    price = promo.value
  } else {
    return null
  }
  const discounted = Object.assign({}, baseProduct, {
    price,
    promoId: promo.id,
    promoBase: {
      price: baseProduct.price,
      hasOriginalPrice: ('originalPrice' in baseProduct),
      originalPrice: ('originalPrice' in baseProduct) ? baseProduct.originalPrice : null,
      hasModgroups: ('modgroups' in baseProduct),
      modgroups: baseProduct.modgroups || null,
    },
  })
  if (basePrice !== null) { discounted.originalPrice = baseProduct.price }
  if (modgroups) { discounted.modgroups = modgroups } else { delete discounted.modgroups }
  return discounted
}

function cartTotalPrice(product) {
  return toPrice(product.price) || 0
}

// Prices a product for `date`. When several promos cover it, the cheapest
// item price wins. With `graceMs`, a promo already on the product is kept if it
// was active `graceMs` ago; grace never puts a new promo on.
export function repriceProduct(product, shopId, date, options) {
  if (!product || !hasTimedPromos(shopId)) { return product }
  const graceMs = (options && options.graceMs) || 0
  const base = stripPromo(product)
  let candidates = promosForShop(shopId).filter((promo) => promoCoversProduct(promo, base) && isPromoActiveAt(promo, date))
  if (!candidates.length && graceMs && product.promoId) {
    const current = getPromoById(product.promoId)
    if (current && promoCoversProduct(current, base) && isPromoActiveAt(current, new Date(date.getTime() - graceMs))) {
      candidates = [current]
    }
  }
  let best = null
  candidates.forEach((promo) => {
    const priced = applyPromo(base, promo)
    if (priced && (!best || cartTotalPrice(priced) < cartTotalPrice(best))) { best = priced }
  })
  const next = best || base
  if (next.promoId === product.promoId && String(next.price) === String(product.price)) { return product }
  return next
}

// "18:00" -> "6pm", "09:30" -> "9:30am"
export function formatClock(hhmm) {
  const total = toMinutes(hhmm)
  const h = Math.floor(total / 60) % 24
  const m = total % 60
  const suffix = h < 12 ? 'am' : 'pm'
  const h12 = (h % 12) || 12
  return m ? `${h12}:${String(m).padStart(2, '0')}${suffix}` : `${h12}${suffix}`
}

// The shop's promos for display (home page banner), in config order.
export function promosForDisplay(shopId) {
  return promosForShop(shopId).map((promo) => Object.assign({}, promo, {
    timeText: `${formatClock(promo.start)} – ${formatClock(promo.end)}`,
  }))
}

// Prices every product, and while a promo is on: moves its categories to the
// top tagged with `promoBadge`, and sets `menu.promoBar` for whole-menu promos.
// Categories remember their place (`promoSortIndex`) so they move back after.
export function applyTimedPromosToMenu(menu, date) {
  try {
    if (!menu || !menu.subMenus || !hasTimedPromos(menu.shopId)) { return menu }
    const active = promosForShop(menu.shopId).filter((promo) => isPromoActiveAt(promo, date))
    const subMenus = menu.subMenus.map((subMenu, index) => {
      const category = (subMenu.name && subMenu.name.en) || ''
      const featuring = active.find((promo) => promo.categories && promo.categories.some((c) => normalize(c) === normalize(category)))
      const promoted = Object.assign({}, subMenu, {
        promoSortIndex: ('promoSortIndex' in subMenu) ? subMenu.promoSortIndex : index,
        products: (subMenu.products || []).map((product) => {
          const tagged = (product.promoCategory === category) ? product : Object.assign({}, product, {promoCategory: category})
          return repriceProduct(tagged, menu.shopId, date)
        })
      })
      if (featuring) {
        promoted.promoBadge = `${featuring.badge || featuring.headline} until ${formatClock(featuring.end)}`
      } else {
        delete promoted.promoBadge
      }
      return promoted
    })
    subMenus.sort((a, b) => ((a.promoBadge ? 0 : 1) - (b.promoBadge ? 0 : 1)) || (a.promoSortIndex - b.promoSortIndex))
    const barPromo = active.find((promo) => !promo.categories && promo.barText)
    const promotedMenu = Object.assign({}, menu, {subMenus})
    if (barPromo) {
      promotedMenu.promoBar = `${barPromo.barText} until ${formatClock(barPromo.end)}`
    } else {
      delete promotedMenu.promoBar
    }
    return promotedMenu
  } catch (e) {
    console.log(e)
    return menu
  }
}

// Changes whenever any of the shop's windows opens or closes; cheap to compare
// on a timer to decide whether the menu needs re-pricing.
export function activePromoKey(shopId, date) {
  return promosForShop(shopId).filter((promo) => isPromoActiveAt(promo, date)).map((promo) => promo.id).join(',')
}
